import React from 'react';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import PinInput from 'react-pin-input';
import { logoVoxel } from '../../imagepath';
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import { ErrorMessage } from '@hookform/error-message';
import { LOGIN_FORM_TYPES } from '../../../appContants.js';
import AppMascot from './AppMascot.jsx';
import ResetPassword from './ResetPassword/ResetPassword.jsx';
import ReverseTimer from '../../../common/ReverseTimer.jsx';
import Loader from '../../../common/loader.jsx';
import useLoginFlow from '../../../hooks/useLoginFlow.js';

const Login = () => {
  const {
    register,
    errors,
    handleSubmit,
    inputRef,
    contextHolder,
    isLoading,
    cognitoUser,
    showPassword,
    formDataForOtp,
    timerKey,
    isOtpTimerActive,
    isRegisterMsg,
    currentStep,
    email,
    togglePasswordVisibility,
    resetLoginFlow,
    renderTitle,
    handleUserStatusSubmit,
    handlePasswordLogin,
    handleNewPasswordSubmit,
    handleResetPasswordSubmit,
    handleResendResetCode,
    handleOtpComplete,
    handleOtpResend,
    setIsOtpTimerActive,
    trustDevice,
    setTrustDevice,
  } = useLoginFlow();

  const renderPasswordToggle = () => (
    <span
      className="profile-views feather-eye-off toggle-password"
      onClick={togglePasswordVisibility}
    >
      <FeatherIcon icon={showPassword ? 'eye-off' : 'eye'} />
    </span>
  );

  const renderTrustDeviceCheckbox = () => (
    <label
      className="login-otp-trust"
      title="Skip email code on this browser next time. Only check once per device."
    >
      <input
        type="checkbox"
        checked={trustDevice}
        onChange={(e) => setTrustDevice(e.target.checked)}
      />
      Trust this device
    </label>
  );

  const renderNewPasswordForm = () => (
    <form onSubmit={handleSubmit(handleNewPasswordSubmit)}>
      <div className="form-group">
        <label>
          Password <span className="login-danger">*</span>
        </label>
        <input
          ref={inputRef}
          className="form-control pass-input"
          type={showPassword ? 'text' : 'password'}
          {...register('password', { required: true })}
        />
        <small style={{ color: 'red' }}>
          {errors.password && <p>Please enter password</p>}
        </small>
        <ErrorMessage errors={errors} name="password" />
        {renderPasswordToggle()}
      </div>
      <div className="form-group login-btn">
        <button className="btn btn-primary btn-block" type="submit">
          Submit
        </button>
      </div>
    </form>
  );

  const renderOtpForm = () => (
    <div className="login-otp-section">
      {isOtpTimerActive && (
        <p className="login-otp-message">
          6-digit code sent to <strong>{formDataForOtp?.email || email}</strong>.
          Valid for 10 minutes.
        </p>
      )}
      <PinInput
        length={6}
        initialValue=""
        type="numeric"
        inputMode="number"
        style={{ padding: 0, margin: '0 0 16px', width: '100%' }}
        inputStyle={{
          width: '48px',
          height: '48px',
          margin: 0,
          padding: 0,
          fontSize: '20px',
          fontWeight: 600,
          textAlign: 'center',
          border: '2px solid rgba(46, 55, 164, 0.1)',
          borderRadius: '10px',
          background: '#fff',
          color: '#333448',
        }}
        inputFocusStyle={{
          borderColor: 'rgb(74 83 199 / 80%)',
          outline: 'none',
          boxShadow: 'none',
        }}
        onComplete={handleOtpComplete}
        autoSelect={true}
        regexCriteria={/^[ A-Za-z0-9_@./#&+-]*$/}
      />
      {renderTrustDeviceCheckbox()}
      <div className="forgotpass login-otp-actions">
        <ReverseTimer
          key={timerKey}
          duration={10 * 60}
          onExpire={() => {}}
          onResend={handleOtpResend}
          onActiveChange={setIsOtpTimerActive}
        />
        <Link onClick={resetLoginFlow}>Back To Login</Link>
      </div>
    </div>
  );

  const renderResetPasswordForm = () => (
    <>
      <div className="alert py-2 px-3 mb-3">
        We sent a reset code to <strong>{email}</strong>.
      </div>
      <ResetPassword
        loading={isLoading}
        onSubmit={handleResetPasswordSubmit}
        email={email}
      />
      <div className="forgotpass">
        <Link onClick={handleResendResetCode}>Resend Code</Link>
        <Link onClick={resetLoginFlow}>Back</Link>
      </div>
    </>
  );

  const renderPasswordForm = () => (
    <form onSubmit={handleSubmit(handlePasswordLogin)}>
      <div className="form-group">
        <label>
          Email <span className="login-danger">*</span>
        </label>
        <input
          className="form-control"
          type="text"
          value={email}
          disabled
          readOnly
        />
      </div>
      <div className="form-group">
        <label>
          Password <span className="login-danger">*</span>
        </label>
        <input
          ref={inputRef}
          className="form-control pass-input"
          type={showPassword ? 'text' : 'password'}
          {...register('password', { required: true })}
        />
        <small style={{ color: 'red' }}>
          {errors.password && <p>Please enter Password</p>}
        </small>
        <ErrorMessage errors={errors} name="password" />
        {renderPasswordToggle()}
      </div>
      <div className="forgotpass">
        <Link to="/forgotpassword">Forgot Password?</Link>
      </div>
      <div className="form-group login-btn">
        <button className="btn btn-primary btn-block" type="submit">
          Submit
        </button>
      </div>
    </form>
  );

  const renderEmailForm = () => (
    <form onSubmit={handleSubmit(handleUserStatusSubmit)}>
      <div className="form-group">
        <label>
          Email <span className="login-danger">*</span>
        </label>
        <input
          className="form-control"
          type="text"
          {...register('email', {
            required: 'Please Enter Your Email!',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Please Enter A Valid Email!',
            },
          })}
        />
        <small style={{ color: 'red' }}>
          {errors.email?.message}
        </small>
      </div>
      <div className="form-group login-btn">
        <button className="btn btn-primary btn-block" type="submit">
          Next
        </button>
      </div>
    </form>
  );

  const renderFormContent = () => {
    if (cognitoUser?.challengeName === LOGIN_FORM_TYPES.NEW_PASSWORD_REQUIRED) {
      return renderNewPasswordForm();
    }
    if (cognitoUser?.challengeName === LOGIN_FORM_TYPES.CUSTOM_CHALLENGE) {
      return renderOtpForm();
    }
    switch (currentStep) {
      case 'RESET_PASSWORD':
        return renderResetPasswordForm();
      case 'PASSWORD':
        return renderPasswordForm();
      default:
        return renderEmailForm();
    }
  };

  return (
    <>
      {contextHolder}
      <div className="main-wrapper login-body">
        <div className="container-fluid px-0">
          <div className="row login-wrap-gradient">
            <AppMascot />
            <div className="col-lg-6 login-wrap-bg">
              <div className="login-wrapper">
                <div className="loginbox">
                  <div className="login-right">
                    <div className="login-right-wrap">
                      <div className="account-logo">
                        <Link to="/admin-dashboard">
                          <img src={logoVoxel} alt="#" style={{ height: 130 }} />
                        </Link>
                      </div>
                      {isRegisterMsg && (
                        <div className="alert py-2 px-3 text-center mb-2">
                          <strong>Thank you for signing up.</strong>
                          <br />
                          Your clinic will be approved by the Voxel team shortly.
                        </div>
                      )}
                      <h2>{renderTitle()}</h2>
                      {renderFormContent()}
                      <div className="next-sign">
                        <p className="account-subtitle">
                          Have You Register Your Clinic?{' '}
                          <Link to="/signup">Sign Up</Link>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isLoading && <Loader />}
    </>
  );
};

export default Login;
